/**
 * Keep the app's safe-area CSS custom properties honest on Android.
 *
 * `index.css` declares these from the standard CSS environment variables:
 *
 *   --safe-area-top:    env(safe-area-inset-top, 0px)
 *   --safe-area-right:  env(safe-area-inset-right, 0px)
 *   --safe-area-bottom: env(safe-area-inset-bottom, 0px)
 *   --safe-area-left:   env(safe-area-inset-left, 0px)
 *
 * On iOS and in browsers that is exactly right — `env()` reports the notch,
 * the Dynamic Island and the home indicator, and nothing here needs to run.
 *
 * Android is the problem. Its WebView only ever exposes a *display cutout*
 * through `env(safe-area-inset-*)`, never the status bar, so the top inset
 * reads 0px even though the app draws edge to edge (it targets SDK 36, and
 * Android 15+ forces edge-to-edge no matter what `StatusBar.overlaysWebView`
 * says). Anything pinned to the top of the viewport therefore renders
 * underneath the clock and the battery icons.
 *
 * MainActivity measures the real insets and exposes them as
 * `window.AndroidSafeArea`. This module copies them into the same four custom
 * properties as an inline style on <html>, which outranks the `:root` rule from
 * index.css. Every existing consumer of `var(--safe-area-top)` — the mobile
 * layout, the bottom navigation, the admin shell — is then correct on both
 * platforms with no per-component branching.
 *
 * Where the bridge is absent (iOS, desktop, a plain browser tab) this is a
 * no-op and `env()` keeps driving the variables.
 */

const PROPERTIES = ['--safe-area-top', '--safe-area-right', '--safe-area-bottom', '--safe-area-left'];

/** Read "top,right,bottom,left" from the native bridge. Null when unavailable. */
const readNativeInsets = () => {
  try {
    const raw = window.AndroidSafeArea?.get?.();
    if (typeof raw !== 'string') return null;

    const values = raw.split(',').map((part) => Number.parseInt(part, 10));
    if (values.length !== 4 || values.some((n) => !Number.isFinite(n) || n < 0)) return null;

    return values;
  } catch {
    // A malformed or missing bridge must never break app start-up.
    return null;
  }
};

const applyNativeInsets = () => {
  const values = readNativeInsets();
  if (!values) return false;

  // All four zero means the bridge has not measured yet (it fires again on the
  // first layout pass). Leave env() in charge rather than pinning everything to
  // 0px, which would be wrong on a device that does report a cutout.
  if (values.every((n) => n === 0)) return false;

  const style = document.documentElement.style;
  values.forEach((value, index) => {
    style.setProperty(PROPERTIES[index], `${value}px`);
  });

  return true;
};

/**
 * Start syncing native insets into CSS. Safe to call once, at app start-up.
 * Returns a teardown function.
 */
export function initSafeAreaInsets() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }

  applyNativeInsets();

  // The native side re-measures on rotation and whenever a system bar shows or
  // hides; it announces that with this event.
  const onChange = () => applyNativeInsets();
  window.addEventListener('androidsafeareachange', onChange);
  window.addEventListener('orientationchange', onChange);
  window.addEventListener('resize', onChange);

  // The very first measurement can land before this module runs. Retry over the
  // first few seconds, then stop — cheap, and it removes the start-up race.
  const timers = [100, 400, 1200, 3000].map((delay) =>
    window.setTimeout(applyNativeInsets, delay)
  );

  return () => {
    window.removeEventListener('androidsafeareachange', onChange);
    window.removeEventListener('orientationchange', onChange);
    window.removeEventListener('resize', onChange);
    timers.forEach(window.clearTimeout);
  };
}

export default initSafeAreaInsets;
